import {
  IconArmchair,
  IconBuilding,
  IconCategory,
  IconChartBar,
  IconCreditCard,
  IconCrown,
  IconDashboard,
  IconDiscount2,
  IconEye,
  IconFolder,
  IconInnerShadowTop,
  IconListDetails,
  IconPackage,
  IconReceipt,
  IconSettings,
  IconShield,
  IconUserCog,
} from "@tabler/icons-react";
import type * as React from "react";
import { Link } from "react-router";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import { OrganizationSwitcher } from "@/components/organizations";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useIsBillingManager } from "@/hooks/use-billing-access";
import { useIsOrgAdmin, useIsSudo } from "@/lib/admin-utils";
import { TOUR_STEP_IDS } from "@/lib/tour-constants";

const data = {
  navMain: [
    {
      title: "sidebar.dashboard",
      url: "/dashboard",
      icon: IconDashboard,
    },
    {
      title: "sidebar.menu.sections",
      url: "/menu/sections",
      icon: IconCategory,
    },
    {
      title: "sidebar.menu.collections",
      url: "/menu/collections",
      icon: IconFolder,
    },
    {
      title: "sidebar.menu.products",
      url: "/menu/products",
      icon: IconPackage,
    },
    {
      title: "sidebar.menu.promotions",
      url: "/menu/promotions",
      icon: IconDiscount2,
      items: [
        {
          title: "sidebar.menu.promotions_custom",
          url: "/menu/promotions/custom",
        },
        {
          title: "sidebar.menu.promotions_section",
          url: "/menu/promotions/section",
        },
        {
          title: "sidebar.menu.promotions_collection",
          url: "/menu/promotions/collection",
        },
      ],
    },
    {
      title: "sidebar.menu.productsView",
      url: "/menu/products-view",
      icon: IconEye,
    },
    {
      title: "sidebar.menu.tables",
      url: "/menu/tables",
      icon: IconArmchair,
    },
    {
      title: "sidebar.orders.title",
      url: "/orders",
      icon: IconReceipt,
      items: [
        {
          title: "sidebar.orders.live",
          url: "/orders",
        },
        {
          title: "sidebar.orders.settings",
          url: "/orders/settings",
        },
        {
          title: "sidebar.orders.riders",
          url: "/orders/riders",
        },
      ],
    },
  ],
  navClouds: [],
  navSecondary: [
    {
      title: "Organizations",
      url: "/organizations",
      icon: IconBuilding,
    },
    {
      title: "Billing",
      url: "/billing",
      icon: IconCreditCard,
    },
    {
      title: "Pricing",
      url: "/pricing",
      icon: IconCrown,
    },
    {
      title: "sidebar.secondary.settings",
      url: "/settings",
      icon: IconSettings,
      id: TOUR_STEP_IDS.SETTINGS_BUTTON,
    },
  ],
  navAdmin: [
    {
      title: "Admin Dashboard",
      url: "/admin",
      icon: IconShield,
    },
    {
      title: "Analytics",
      url: "/admin/analytics",
      icon: IconChartBar,
    },
    {
      title: "Plan Sync",
      url: "/admin/plans",
      icon: IconListDetails,
    },
    {
      title: "User Management",
      url: "/admin/users",
      icon: IconUserCog,
    },
  ],
  documents: [],
};

const ADMIN_ONLY_NAV_URLS = new Set(["/orders/riders", "/orders/settings", "/organizations"]);
const BILLING_MANAGER_ONLY_NAV_URLS = new Set(["/billing"]);

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const isAppAdmin = useIsSudo();
  const isOrgAdmin = useIsOrgAdmin();
  const isBillingManager = useIsBillingManager();

  const navMain = isOrgAdmin
    ? data.navMain
    : data.navMain.map((item) =>
        item.items
          ? {
              ...item,
              items: item.items.filter((sub) => !ADMIN_ONLY_NAV_URLS.has(sub.url)),
            }
          : item,
      );

  const navSecondary = data.navSecondary.filter((item) => {
    if (ADMIN_ONLY_NAV_URLS.has(item.url) && !isOrgAdmin) return false;
    if (BILLING_MANAGER_ONLY_NAV_URLS.has(item.url) && !isBillingManager) return false;
    return true;
  });

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="space-y-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:p-1.5!">
              <Link to="/dashboard">
                <IconInnerShadowTop className="size-5!" />
                <span className="font-semibold text-base">La Nueva Carta Digital</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="px-2">
          <OrganizationSwitcher
            className="w-full"
            onManageOrganizations={() => {
              // This will be handled by the OrganizationSwitcher's routing
            }}
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
        {isAppAdmin && <NavSecondary items={data.navAdmin} title="Platform Admin" />}
        <NavSecondary className="mt-auto" items={navSecondary} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
